/**
 * Project Dwelling Service — Projects v2 Phase 3 (P3.1).
 *
 * CRUD on the `projects/{projectId}/dwellings/{dwellingId}` sub-collection.
 * Mirrors the structure of `project-activity.service.ts` (P2.2): tenant
 * scoping via localStorage canonical fallback, sanitize helper for
 * Timestamp → ISO conversion on read, transactional dual-write to keep
 * `Project.dwellingCount` in sync.
 *
 * Used by:
 *   - useProjectDwellings hook (P3.2)
 *   - AddDwellingModal + DwellingDetailPage (P3.3 / P3.4 / P3.5)
 *   - functions/src/tasks/auto-action-handlers.ts createPerDwellingPreWireJobs
 *     once Phase 6 reaches it (server-side parallel implementation)
 *
 * Soft-delete: archiveDwelling sets status to 'On Hold' rather than deleting
 * the doc. The grid hides 'On Hold' dwellings from the active list; an
 * "Archived" disclosure on the grid shows them with a Restore button.
 * dwellingCount tracks active dwellings only — increments on create +
 * restore, decrements on archive.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  Timestamp,
  where,
  writeBatch,
  type DocumentData,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import type { ProjectDwelling } from '@/types/project.types';
import { appendActivityEntry } from './project-activity.service';
import { scheduleRecalc } from './project-recalc-trigger';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readTenantId(): string {
  if (typeof window === 'undefined') return 'techery-au';
  return getTenantIdSync();
}

function readActor(): { id: string; name: string } {
  if (typeof window === 'undefined') return { id: '', name: 'system' };
  return {
    id: localStorage.getItem('user_id') ?? '',
    name: localStorage.getItem('user_name') ?? 'Unknown',
  };
}

function toISOString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value && typeof value === 'object' && 'seconds' in (value as Record<string, unknown>)) {
    return new Date((value as { seconds: number }).seconds * 1000).toISOString();
  }
  return new Date().toISOString();
}

/** Coerce any Timestamp-shaped fields on a dwelling doc to ISO strings.
 *  Same defensive pattern as sanitizeFault — admin-SDK writes can land
 *  Timestamp objects that crash React rendering. */
function sanitizeDwelling(raw: Record<string, unknown>): ProjectDwelling {
  const sanitized = { ...raw };
  if (sanitized.createdAt && typeof sanitized.createdAt !== 'string') {
    sanitized.createdAt = toISOString(sanitized.createdAt);
  }
  if (sanitized.updatedAt && typeof sanitized.updatedAt !== 'string') {
    sanitized.updatedAt = toISOString(sanitized.updatedAt);
  }
  return sanitized as unknown as ProjectDwelling;
}

/** Strip undefined values — Firestore rejects them. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj) as [keyof T, T[keyof T]][]) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getDwelling(
  projectId: string,
  dwellingId: string
): Promise<ProjectDwelling | null> {
  const ref = doc(db, 'projects', projectId, 'dwellings', dwellingId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return sanitizeDwelling({ id: snap.id, ...snap.data() });
}

/** One-shot list — used where onSnapshot isn't appropriate (e.g. server-side
 *  cloud-task handlers parallel impls, or admin tooling). */
export async function listDwellings(projectId: string): Promise<ProjectDwelling[]> {
  const tenantId = readTenantId();
  const q = query(
    collection(db, 'projects', projectId, 'dwellings'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('order', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => sanitizeDwelling({ id: d.id, ...d.data() }));
}

/** Real-time subscription. Returns unsubscribe. `onError` lets the caller
 *  distinguish a genuine empty list from a failed subscription (permission /
 *  index error) — without it a failure renders as "No dwellings yet". */
export function subscribeDwellings(
  projectId: string,
  cb: (dwellings: ProjectDwelling[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const tenantId = readTenantId();
  const q = query(
    collection(db, 'projects', projectId, 'dwellings'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('order', 'asc')
  );
  return onSnapshot(
    q,
    (snap: QuerySnapshot<DocumentData>) => {
      const list = snap.docs.map((d) => sanitizeDwelling({ id: d.id, ...d.data() }));
      cb(list);
    },
    (err) => {
      console.warn(`[project-dwelling] subscribe error on project ${projectId}:`, err);
      if (onError) onError(err as Error);
      else cb([]);
    }
  );
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Create a dwelling. Atomic: writes the sub-collection doc + increments
 * Project.dwellingCount inside one runTransaction so the counter never
 * drifts. Computes `order` as max(existing) + 1 inside the transaction so
 * concurrent creates don't collide.
 *
 * The activity entry is written via appendActivityEntry AFTER the
 * transaction commits — appendActivityEntry has its own transaction
 * (recentActivity[] mirror) and Firestore doesn't support nested
 * transactions. If the activity write fails, the dwelling create still
 * succeeds; the activity entry is best-effort observability, not a
 * correctness gate.
 */
export async function createDwelling(
  projectId: string,
  data: Omit<ProjectDwelling, 'id' | 'projectId' | 'order' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const tenantId = readTenantId();
  const dwellingId = crypto.randomUUID();
  const now = new Date().toISOString();
  // Use Date.now() as the initial order — monotonically increasing per
  // create call so new dwellings always sort to the end. reorderDwellings
  // re-numbers to 0-indexed sequential when the user drags. Avoids the
  // tx-can't-query-collection limitation in the Firestore web SDK
  // without coordination.
  const initialOrder = Date.now();

  const projectRef = doc(db, 'projects', projectId);
  const dwellingRef = doc(db, 'projects', projectId, 'dwellings', dwellingId);

  await runTransaction(db, async (tx) => {
    const projectSnap = await tx.get(projectRef);
    if (!projectSnap.exists()) {
      throw new Error(`Project ${projectId} not found`);
    }
    const projectData = projectSnap.data();
    const projectTenant = (projectData.tenantId as string | undefined) ?? 'techery-au';
    if (projectTenant !== tenantId) {
      throw new Error(
        `Tenant mismatch — project="${projectTenant}", caller="${tenantId}"`
      );
    }
    const currentDwellingCount =
      (projectData.dwellingCount as number | undefined) ?? 0;

    const fullDwelling = {
      ...data,
      id: dwellingId,
      projectId,
      tenantId,
      order: initialOrder,
      createdAt: now,
      updatedAt: now,
    };

    tx.set(dwellingRef, stripUndefined(fullDwelling as Record<string, unknown>));
    // dwellingCount tracks ACTIVE dwellings only (archive decrements it). A
    // dwelling created directly into 'On Hold' must not be counted, or the
    // counter drifts above what the active grid shows and archiving it later
    // is a no-op that never corrects the over-count.
    const createdActive = (data as { status?: string }).status !== 'On Hold';
    tx.update(projectRef, {
      dwellingCount: currentDwellingCount + (createdActive ? 1 : 0),
      updatedAt: now,
    });
  });

  // Best-effort activity entry. Failure here doesn't roll back the
  // dwelling — observability should never block the primary write.
  const actor = readActor();
  await appendActivityEntry(projectId, {
    type: 'dwelling_added',
    actor: actor.id,
    actorName: actor.name,
    action: `Added dwelling ${data.label}`,
    details: data.shortCode ? `Short code: ${data.shortCode}` : undefined,
    dwellingId,
  }).catch((err) => {
    console.warn(`[project-dwelling] activity entry failed for ${dwellingId}:`, err);
  });

  return dwellingId;
}

export async function updateDwelling(
  projectId: string,
  dwellingId: string,
  updates: Partial<Omit<ProjectDwelling, 'id' | 'projectId' | 'createdAt'>>
): Promise<void> {
  const ref = doc(db, 'projects', projectId, 'dwellings', dwellingId);
  const cleaned = stripUndefined({
    ...updates,
    updatedAt: new Date().toISOString(),
  } as Record<string, unknown>);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Dwelling ${dwellingId} not found on project ${projectId}`);
    }
    tx.update(ref, cleaned);
  });
}

/**
 * Soft-delete. Sets status to 'On Hold' (no dedicated 'Archived' value in
 * the P1.3c status union — reusing 'On Hold' keeps the type narrow).
 * Decrements Project.dwellingCount in the same transaction so the counter
 * tracks active dwellings only.
 */
export async function archiveDwelling(projectId: string, dwellingId: string): Promise<void> {
  const projectRef = doc(db, 'projects', projectId);
  const dwellingRef = doc(db, 'projects', projectId, 'dwellings', dwellingId);
  let dwellingLabel = '';

  await runTransaction(db, async (tx) => {
    const dwellingSnap = await tx.get(dwellingRef);
    if (!dwellingSnap.exists()) {
      throw new Error(`Dwelling ${dwellingId} not found`);
    }
    const dwellingData = dwellingSnap.data();
    if (dwellingData.status === 'On Hold') {
      // Idempotent — already archived. Don't double-decrement.
      return;
    }
    dwellingLabel = (dwellingData.label as string | undefined) ?? '';

    const projectSnap = await tx.get(projectRef);
    const currentCount =
      (projectSnap.exists() ? (projectSnap.data().dwellingCount as number | undefined) : 0) ?? 0;

    tx.update(dwellingRef, {
      status: 'On Hold',
      // Snapshot the pre-archive status so restore can return the dwelling to
      // its real lifecycle stage instead of always resetting to 'Planned'
      // (a Commissioned / Handed Over unit must not silently revert).
      statusBeforeArchive: dwellingData.status ?? 'Planned',
      updatedAt: new Date().toISOString(),
    });
    tx.update(projectRef, {
      dwellingCount: Math.max(0, currentCount - 1),
      updatedAt: new Date().toISOString(),
    });
  });

  if (dwellingLabel) {
    const actor = readActor();
    await appendActivityEntry(projectId, {
      type: 'dwelling_archived',
      actor: actor.id,
      actorName: actor.name,
      action: `Archived dwelling ${dwellingLabel}`,
      dwellingId,
    }).catch((err) => {
      console.warn(`[project-dwelling] activity entry failed for ${dwellingId}:`, err);
    });
  }

  // P9a.3 — archiving a dwelling removes its cost from the project total.
  scheduleRecalc(projectId);
}

/**
 * Inverse of archive — flips status back to 'Planned' and re-increments
 * Project.dwellingCount. Used by the grid's "Archived" section's Restore
 * button.
 */
export async function restoreDwelling(projectId: string, dwellingId: string): Promise<void> {
  const projectRef = doc(db, 'projects', projectId);
  const dwellingRef = doc(db, 'projects', projectId, 'dwellings', dwellingId);
  let dwellingLabel = '';

  await runTransaction(db, async (tx) => {
    const dwellingSnap = await tx.get(dwellingRef);
    if (!dwellingSnap.exists()) {
      throw new Error(`Dwelling ${dwellingId} not found`);
    }
    const dwellingData = dwellingSnap.data();
    if (dwellingData.status !== 'On Hold') {
      // Idempotent — already active. Don't double-increment.
      return;
    }
    dwellingLabel = (dwellingData.label as string | undefined) ?? '';

    const projectSnap = await tx.get(projectRef);
    const currentCount =
      (projectSnap.exists() ? (projectSnap.data().dwellingCount as number | undefined) : 0) ?? 0;

    // Restore to the pre-archive lifecycle status when we captured one;
    // fall back to 'Planned' for dwellings archived before this field existed.
    const restoredStatus =
      (dwellingData.statusBeforeArchive as ProjectDwelling['status'] | undefined) ?? 'Planned';

    tx.update(dwellingRef, {
      status: restoredStatus,
      statusBeforeArchive: null,
      updatedAt: new Date().toISOString(),
    });
    tx.update(projectRef, {
      dwellingCount: currentCount + 1,
      updatedAt: new Date().toISOString(),
    });
  });

  if (dwellingLabel) {
    const actor = readActor();
    await appendActivityEntry(projectId, {
      type: 'dwelling_restored',
      actor: actor.id,
      actorName: actor.name,
      action: `Restored dwelling ${dwellingLabel}`,
      dwellingId,
    }).catch((err) => {
      console.warn(`[project-dwelling] activity entry failed for ${dwellingId}:`, err);
    });
  }

  // P9a.3 — restoring a dwelling adds its cost back into the project total.
  scheduleRecalc(projectId);
}

/**
 * Bulk-write `order` field on each dwelling. Order in the input array is
 * the new visual order (0-indexed). Uses writeBatch for efficiency —
 * atomicity isn't critical because order is purely cosmetic (drift between
 * writes just means one dwelling appears in the wrong slot until the
 * snapshot listener catches up; no data integrity issue).
 */
export async function reorderDwellings(
  projectId: string,
  orderedIds: string[]
): Promise<void> {
  if (orderedIds.length === 0) return;
  const batch = writeBatch(db);
  const now = new Date().toISOString();
  orderedIds.forEach((id, index) => {
    const ref = doc(db, 'projects', projectId, 'dwellings', id);
    batch.update(ref, { order: index, updatedAt: now });
  });
  await batch.commit();
}
